import React, { useState, useMemo, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import ConsultationCTA from '../../components/ui/ConsultationCTA';
import TeamMemberCard from './components/TeamMemberCard';
import TeamFilter from './components/TeamFilter';
import TeamStats from './components/TeamStats';
import TeamSection from './components/TeamSection';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const TeamProfiles = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeExpertise, setActiveExpertise] = useState('');
  const [activeExperience, setActiveExperience] = useState('all');
  const [expandedSections, setExpandedSections] = useState({
    leadership: true,
    'ai-engineering': false,
    'software-development': false,
    analytics: false,
    cybersecurity: false
  });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Mock team data
  const teamData = {
    leadership: [
      {
        id: 'l1',
        name: 'Dr. Sarah Chen',
        title: 'Chief Executive Officer',
        image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400&h=400&fit=crop&crop=face',
        bio: `Visionary leader with 20+ years in AI and enterprise technology. Former VP of AI at Microsoft, led digital transformation initiatives for Fortune 100 companies. PhD in Computer Science from Stanford, published 50+ research papers in machine learning and AI ethics.`,
        expertise: ['AI Strategy', 'Digital Transformation', 'Executive Leadership', 'Machine Learning', 'AI Ethics'],
        experience: 20,
        certifications: 8,
        linkedIn: 'https://linkedin.com/in/sarahchen',
        email: 'sarah.chen@rcegai.com',
        projects: [
          'Led $50M AI transformation for global retailer',
          'Architected ML platform serving 100M+ users',
          'Established AI governance framework for Fortune 500'
        ],
        testimonial: {
          quote: 'Sarah\'s strategic vision and technical depth transformed our entire approach to AI implementation.',
          client: 'James Wilson',
          company: 'Global Manufacturing Corp'
        }
      },
      {
        id: 'l2',
        name: 'Michael Rodriguez',
        title: 'Chief Technology Officer',
        image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face',
        bio: `Technology architect with deep expertise in scalable AI systems and cloud infrastructure. Former Principal Engineer at Google Cloud, specialized in building enterprise-grade AI platforms. MS in Computer Engineering from MIT.`,
        expertise: ['Cloud Architecture', 'AI Infrastructure', 'DevOps', 'System Design', 'Kubernetes'],
        experience: 18,
        certifications: 12,
        linkedIn: 'https://linkedin.com/in/michaelrodriguez',
        email: 'michael.rodriguez@rcegai.com',
        projects: [
          'Built AI platform processing 1B+ daily requests',
          'Designed multi-cloud architecture for enterprise AI',
          'Led migration of legacy systems to cloud-native AI'
        ],
        testimonial: {
          quote: 'Michael\'s technical leadership ensured our AI platform could scale to meet enterprise demands.',
          client: 'Lisa Park',
          company: 'TechCorp Industries'
        }
      },
      {
        id: 'l3',
        name: 'Dr. Priya Patel',
        title: 'Chief Data Officer',
        image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=400&fit=crop&crop=face',
        bio: `Data science leader with expertise in advanced analytics and AI model development. Former Head of Data Science at Amazon, specialized in recommendation systems and predictive analytics. PhD in Statistics from UC Berkeley.`,
        expertise: ['Data Science', 'Machine Learning', 'Statistical Modeling', 'Big Data', 'Analytics Strategy'],
        experience: 16,
        certifications: 10,
        linkedIn: 'https://linkedin.com/in/priyapatel',
        email: 'priya.patel@rcegai.com',
        projects: [
          'Developed recommendation engine for 50M+ users',
          'Built predictive models improving ROI by 300%',
          'Established data governance for enterprise clients'
        ],
        testimonial: {
          quote: 'Priya\'s analytical expertise helped us unlock insights we never knew existed in our data.',
          client: 'Robert Kim',
          company: 'Financial Services Inc'
        }
      }
    ],
    'ai-engineering': [
      {
        id: 'ai1',
        name: 'David Kim',
        title: 'Senior AI Engineer',
        image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face',
        bio: `Specialized in deep learning and computer vision applications. Expert in TensorFlow, PyTorch, and production ML systems.`,
        expertise: ['Deep Learning', 'Computer Vision', 'TensorFlow', 'PyTorch', 'MLOps'],
        experience: 12,
        certifications: 6,
        linkedIn: 'https://linkedin.com/in/davidkim',
        email: 'david.kim@rcegai.com',
        projects: [
          'Built real-time image recognition system',
          'Developed autonomous vehicle perception models',
          'Created medical imaging diagnostic AI'
        ],
        testimonial: {
          quote: 'David\'s computer vision expertise was crucial for our manufacturing quality control system.',
          client: 'Maria Santos',
          company: 'Manufacturing Excellence'
        }
      },
      {
        id: 'ai2',
        name: 'Dr. Emily Watson',
        title: 'NLP Research Scientist',
        image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop&crop=face',
        bio: `Natural Language Processing expert with focus on conversational AI and language understanding systems.`,
        expertise: ['Natural Language Processing', 'Conversational AI', 'BERT', 'GPT', 'Language Models'],
        experience: 10,
        certifications: 8,
        linkedIn: 'https://linkedin.com/in/emilywatson',
        email: 'emily.watson@rcegai.com',
        projects: [
          'Developed multilingual chatbot platform',
          'Built document analysis and summarization system',
          'Created sentiment analysis for social media'
        ],
        testimonial: {
          quote: 'Emily\'s NLP solutions transformed how we interact with our customers.',
          client: 'John Davis',
          company: 'Customer Service Solutions'
        }
      },
      {
        id: 'ai3',
        name: 'Alex Thompson',
        title: 'Machine Learning Engineer',
        image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop&crop=face',
        bio: `Full-stack ML engineer specializing in recommendation systems and predictive analytics for enterprise applications.`,
        expertise: ['Machine Learning', 'Recommendation Systems', 'Python', 'Scikit-learn', 'Apache Spark'],
        experience: 8,
        certifications: 5,
        linkedIn: 'https://linkedin.com/in/alexthompson',
        email: 'alex.thompson@rcegai.com',
        projects: [
          'Built personalization engine for e-commerce',
          'Developed fraud detection system',
          'Created demand forecasting models'
        ],
        testimonial: {
          quote: 'Alex\'s recommendation system increased our conversion rates by 40%.',
          client: 'Sarah Johnson',
          company: 'E-commerce Plus'
        }
      }
    ],
    'software-development': [
      {
        id: 'sd1',
        name: 'Jennifer Liu',
        title: 'Senior Full-Stack Developer',
        image: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=400&h=400&fit=crop&crop=face',
        bio: `Full-stack developer with expertise in React, Node.js, and cloud-native applications. Specialized in building scalable web platforms.`,
        expertise: ['React', 'Node.js', 'TypeScript', 'AWS', 'Docker'],
        experience: 9,
        certifications: 7,
        linkedIn: 'https://linkedin.com/in/jenniferliu',
        email: 'jennifer.liu@rcegai.com',
        projects: [
          'Built enterprise dashboard serving 10K+ users',
          'Developed microservices architecture',
          'Created real-time analytics platform'
        ],
        testimonial: {
          quote: 'Jennifer delivered a robust platform that exceeded all our performance expectations.',
          client: 'Mark Brown',
          company: 'Enterprise Solutions'
        }
      },
      {
        id: 'sd2',
        name: 'Carlos Martinez',
        title: 'DevOps Engineer',
        image: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=400&h=400&fit=crop&crop=face',
        bio: `DevOps specialist focused on CI/CD pipelines, infrastructure automation, and cloud deployment strategies.`,
        expertise: ['DevOps', 'Kubernetes', 'Terraform', 'Jenkins', 'AWS'],
        experience: 11,
        certifications: 9,
        linkedIn: 'https://linkedin.com/in/carlosmartinez',
        email: 'carlos.martinez@rcegai.com',
        projects: [
          'Automated deployment for 50+ microservices',
          'Built multi-region disaster recovery system',
          'Implemented zero-downtime deployment pipeline'
        ],
        testimonial: {
          quote: 'Carlos transformed our deployment process, reducing release time from days to hours.',
          client: 'Anna Lee',
          company: 'Tech Innovations'
        }
      },
      {
        id: 'sd3',
        name: 'Rachel Green',
        title: 'Mobile App Developer',
        image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400&h=400&fit=crop&crop=face',
        bio: `Mobile development expert specializing in React Native and Flutter for cross-platform enterprise applications.`,
        expertise: ['React Native', 'Flutter', 'iOS', 'Android', 'Mobile UI/UX'],
        experience: 7,
        certifications: 4,
        linkedIn: 'https://linkedin.com/in/rachelgreen',
        email: 'rachel.green@rcegai.com',
        projects: [
          'Developed enterprise mobile app with 1M+ downloads',
          'Built offline-capable field service application',
          'Created AR-enabled product visualization app'
        ],
        testimonial: {
          quote: 'Rachel\'s mobile expertise helped us reach customers on every platform seamlessly.',
          client: 'Tom Wilson',
          company: 'Mobile First Corp'
        }
      }
    ],
    analytics: [
      {
        id: 'an1',
        name: 'Dr. James Anderson',
        title: 'Senior Data Scientist',
        image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face',
        bio: `Data scientist with expertise in statistical modeling, predictive analytics, and business intelligence solutions.`,
        expertise: ['Data Science', 'Statistical Modeling', 'R', 'Python', 'Tableau'],
        experience: 13,
        certifications: 8,
        linkedIn: 'https://linkedin.com/in/jamesanderson',
        email: 'james.anderson@rcegai.com',
        projects: [
          'Built customer churn prediction model',
          'Developed pricing optimization algorithm',
          'Created executive dashboard for C-suite'
        ],
        testimonial: {
          quote: 'James\' analytical insights drove a 25% increase in customer retention.',
          client: 'Linda Davis',
          company: 'Retail Analytics Co'
        }
      },
      {
        id: 'an2',
        name: 'Sophie Chen',
        title: 'Business Intelligence Analyst',
        image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=400&fit=crop&crop=face',
        bio: `BI specialist focused on data visualization, reporting automation, and self-service analytics platforms.`,
        expertise: ['Business Intelligence', 'Power BI', 'SQL', 'Data Visualization', 'ETL'],
        experience: 6,
        certifications: 5,
        linkedIn: 'https://linkedin.com/in/sophiechen',
        email: 'sophie.chen@rcegai.com',
        projects: [
          'Automated reporting for 100+ stakeholders',
          'Built self-service analytics platform',
          'Created real-time operational dashboards'
        ],
        testimonial: {
          quote: 'Sophie\'s BI solutions gave us visibility into metrics we never tracked before.',
          client: 'Mike Johnson',
          company: 'Operations Excellence'
        }
      }
    ],
    cybersecurity: [
      {
        id: 'cs1',
        name: 'Robert Taylor',
        title: 'Cybersecurity Architect',
        image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face',
        bio: `Cybersecurity expert specializing in enterprise security architecture, threat detection, and compliance frameworks.`,
        expertise: ['Cybersecurity', 'Network Security', 'CISSP', 'Penetration Testing', 'Compliance'],
        experience: 15,
        certifications: 12,
        linkedIn: 'https://linkedin.com/in/roberttaylor',
        email: 'robert.taylor@rcegai.com',
        projects: [
          'Designed security architecture for financial services',
          'Implemented zero-trust network model',
          'Led SOC 2 compliance initiative'
        ],
        testimonial: {
          quote: 'Robert\'s security expertise protected us from multiple sophisticated threats.',
          client: 'Karen White',
          company: 'Secure Financial'
        }
      },
      {
        id: 'cs2',
        name: 'Lisa Wang',
        title: 'Security Analyst',
        image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop&crop=face',
        bio: `Security analyst focused on threat intelligence, incident response, and security monitoring systems.`,
        expertise: ['Threat Intelligence', 'Incident Response', 'SIEM', 'Forensics', 'Risk Assessment'],
        experience: 8,
        certifications: 6,
        linkedIn: 'https://linkedin.com/in/lisawang',
        email: 'lisa.wang@rcegai.com',
        projects: [
          'Built threat detection system with ML',
          'Established incident response procedures',
          'Conducted security risk assessments'
        ],
        testimonial: {
          quote: 'Lisa\'s proactive monitoring prevented a major security breach.',
          client: 'David Miller',
          company: 'Healthcare Systems'
        }
      }
    ]
  };

  const filteredTeamData = useMemo(() => {
    if (activeFilter === 'all') {
      return teamData;
    }
    
    return {
      [activeFilter]: teamData[activeFilter] || []
    };
  }, [activeFilter, teamData]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const getSectionConfig = (sectionId) => {
    const configs = {
      leadership: {
        title: 'Leadership Team',
        icon: 'Crown',
        description: 'Visionary leaders driving AI innovation and business transformation'
      },
      'ai-engineering': {
        title: 'AI Engineering',
        icon: 'Brain',
        description: 'Machine learning experts building intelligent systems'
      },
      'software-development': {
        title: 'Software Development',
        icon: 'Code',
        description: 'Full-stack developers creating scalable applications'
      },
      analytics: {
        title: 'Analytics & Data Science',
        icon: 'BarChart3',
        description: 'Data scientists uncovering insights and driving decisions'
      },
      cybersecurity: {
        title: 'Cybersecurity',
        icon: 'Shield',
        description: 'Security experts protecting digital assets and infrastructure'
      }
    };
    
    return configs[sectionId] || { title: sectionId, icon: 'Users', description: '' };
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="max-w-7xl mx-auto px-6 py-8">
        <Breadcrumb />
        
        {/* Page Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="w-12 h-12 rounded-full gradient-primary flex items-center justify-center">
              <Icon name="Users" size={24} color="white" />
            </div>
            <h1 className="text-4xl font-bold text-foreground">Meet Our Expert Team</h1>
          </div>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            World-class AI consultants, engineers, and strategists dedicated to transforming your business through innovative technology solutions.
          </p>
        </div>

        {/* Team Statistics */}
        <TeamStats />

        {/* Team Filters */}
        <TeamFilter
          activeFilter={activeFilter}
          onFilterChange={setActiveFilter}
          activeExpertise={activeExpertise}
          onExpertiseChange={setActiveExpertise}
          activeExperience={activeExperience}
          onExperienceChange={setActiveExperience}
          isMobile={isMobile}
        />

        {/* Leadership Team - Special Layout */}
        {(activeFilter === 'all' || activeFilter === 'leadership') && filteredTeamData.leadership && (
          <div className="mb-16">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-foreground mb-4">Leadership Team</h2>
              <p className="text-muted-foreground text-lg">
                Visionary leaders with decades of experience in AI and enterprise technology
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredTeamData.leadership.map((member) => (
                <TeamMemberCard
                  key={member.id}
                  member={member}
                  variant="leadership"
                />
              ))}
            </div>
          </div>
        )}

        {/* Team Sections */}
        {Object.entries(filteredTeamData).map(([sectionId, members]) => {
          if (sectionId === 'leadership' || !members || members.length === 0) {
            return null;
          }
          
          const config = getSectionConfig(sectionId);
          
          return (
            <TeamSection
              key={sectionId}
              title={config.title}
              members={members}
              icon={config.icon}
              description={config.description}
              isExpanded={expandedSections[sectionId]}
              onToggleExpand={() => toggleSection(sectionId)}
              activeExpertise={activeExpertise}
              activeExperience={activeExperience}
            />
          );
        })}

        {/* Join Our Team CTA */}
        <div className="bg-gradient-to-r from-primary to-secondary rounded-xl p-8 text-center text-white mb-12">
          <div className="max-w-2xl mx-auto">
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
              <Icon name="UserPlus" size={32} color="white" />
            </div>
            <h2 className="text-3xl font-bold mb-4">Join Our Growing Team</h2>
            <p className="text-white/90 mb-6 text-lg">
              We're always looking for talented individuals who share our passion for AI innovation and client success.
            </p>
            <div className="flex items-center justify-center space-x-4">
              <Button
                variant="secondary"
                size="lg"
                iconName="Briefcase"
                iconPosition="left"
                onClick={() => window.open('mailto:careers@rcegai.com', '_blank')}
              >
                View Open Positions
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="Send"
                iconPosition="left"
                onClick={() => window.open('mailto:careers@rcegai.com', '_blank')}
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
              >
                Submit Resume
              </Button>
            </div>
          </div>
        </div>

        {/* Consultation CTA */}
        <ConsultationCTA variant="inline" context="team" />
      </main>

      {/* Floating Consultation CTA */}
      <ConsultationCTA variant="floating" context="team" />
    </div>
  );
};

export default TeamProfiles;